import { HelpArticleBody } from "@/components/registrant/help/help-article-body";
import { HelpRelatedArticles } from "@/components/registrant/help/help-portal";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  GOVERNMENT_HELP_PORTAL,
  getGovernmentHelpArticle,
} from "@/lib/government-help";
import type { HelpPortalId } from "@/lib/help-portal";
import {
  INSPECTION_HELP_PORTAL,
  getInspectionHelpArticle,
} from "@/lib/inspection-help";
import { OMT_AMS_HELP_PORTAL, getOmtAmsHelpArticle } from "@/lib/omt-ams-help";
import {
  OPERATOR_HELP_PORTAL,
  getOperatorHelpArticle,
} from "@/lib/operator-help";
import { REGISTRANT_HELP_PORTAL, getHelpArticle } from "@/lib/registrant-help";
import type { AnyHelpArticleId } from "@/lib/registrant-help/types";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { CircleHelp } from "lucide-react";
import { useState } from "react";

interface ContextHelpButtonProps {
  articleId: AnyHelpArticleId;
  /** Visible label. Defaults to "Help". */
  label?: string;
  className?: string;
  variant?: "outline" | "ghost" | "link";
  /** Icon-only control; label is used as the accessible name. */
  iconOnly?: boolean;
  /** Which portal catalog and Help Center links to use. */
  helpPortal?: HelpPortalId;
}

function resolveCatalog(helpPortal: HelpPortalId) {
  switch (helpPortal) {
    case "omt-ams":
      return OMT_AMS_HELP_PORTAL;
    case "government":
      return GOVERNMENT_HELP_PORTAL;
    case "operator":
      return OPERATOR_HELP_PORTAL;
    case "inspection":
      return INSPECTION_HELP_PORTAL;
    default:
      return REGISTRANT_HELP_PORTAL;
  }
}

function resolveArticle(helpPortal: HelpPortalId, articleId: string) {
  switch (helpPortal) {
    case "omt-ams":
      return getOmtAmsHelpArticle(articleId);
    case "government":
      return getGovernmentHelpArticle(articleId);
    case "operator":
      return getOperatorHelpArticle(articleId);
    case "inspection":
      return getInspectionHelpArticle(articleId);
    default:
      return getHelpArticle(articleId);
  }
}

export function ContextHelpButton({
  articleId,
  label = "Help",
  className,
  variant = "outline",
  iconOnly = false,
  helpPortal = "registrant",
}: ContextHelpButtonProps) {
  const [open, setOpen] = useState(false);
  const catalog = resolveCatalog(helpPortal);
  const article = resolveArticle(helpPortal, articleId);
  if (!article) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={iconOnly ? "icon" : "default"}
        className={cn(iconOnly ? "shrink-0" : undefined, className)}
        onClick={() => setOpen(true)}
        aria-label={`${article.title} help`}
      >
        <CircleHelp className={iconOnly ? "h-4 w-4" : "h-4 w-4 mr-2"} />
        {iconOnly ? null : label}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{article.title}</SheetTitle>
            <SheetDescription>{article.summary}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-6">
            <HelpArticleBody article={article} />
            <HelpRelatedArticles article={article} catalog={catalog} />
          </div>
          <div className="border-t px-4 py-3">
            <Button variant="outline" className="w-full" asChild>
              <Link to={catalog.articleTo} params={{ articleId: article.id }}>
                Open in Help Center
              </Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
